import Button from 'components/commons/button/Button';
import InputNumber from 'components/commons/input/InputNumber';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { createManualAsset } from 'config/api';
import { formatDate, formatNumber } from 'utils/formatter';
import {
  ManualAssetType,
  manualAssetType,
  manualAssetTypes,
} from 'components/features/utils/manualAssetType';
import { useRouting } from 'hooks/useRouting';
import { useGlobalOneShot } from 'hooks/useGlobalOneShot';

const minAmount = 1;
const maxAmount = 99999999999;

type From = 'advisor' | 'top' | 'renewal' | 'accountLinkedInfo';

export const linkToOptionalAssetRegistration = (from: From) => {
  return {
    pathname: '/optionalassetregistration',
    state: { referrer: from },
  };
};

type Props = Record<string, unknown>;

const OptionalAssetRegistration: React.FC<Props> = () => {
  const [assetName, setAssetName] = useState('');
  const [assetType, setAssetType] = useState<ManualAssetType>(
    manualAssetType.預金_MRF
  );
  const [assetAmount, setAssetAmount] = useState({
    valid: true,
    value: 0,
  });
  const location = useLocation<{ referrer: From }>();
  const routing = useRouting();
  const [disabled, setDisabled] = useState(false);
  const oneShot = useGlobalOneShot('optionalAssetRegistration');

  function registration() {
    oneShot.call(
      async () => {
        await createManualAsset(assetName, assetType, assetAmount.value);

        location.state.referrer === 'accountLinkedInfo'
          ? // 口座連携案内ページから遷移した場合は/homeへ戻す
            routing.pushRouting({ path: '/home' }, true)
          : // topから遷移した場合は2つ前に戻す、資産目標サポートから遷移した場合は1つ前に戻す
            routing.backRouting(
              true,
              location.state.referrer === 'top' ? 2 : 1
            );
      },
      true,
      () => setDisabled(true)
    );
  }

  function handleRegister() {
    registration();
  }

  function cancel() {
    oneShot.call(() => {
      routing.backRouting(true);
    });
  }

  function handleClickOptionalAssetRenewal() {
    oneShot.call(() => {
      routing.replaceRouting({ path: '/optionalassetrenewal' }, true);
    });
  }

  function handleClickSecurityAccounts() {
    oneShot.call(() => {
      routing.pushRouting({ path: '/securityaccounts' }, true);
    });
  }

  function customValidate(value: string, valueAsNumber: number) {
    return /^([1-9][0-9]*)$/.test(value);
  }

  const showLink =
    location.state.referrer === 'advisor' ||
    location.state.referrer === 'renewal';

  return (
    <>
      <main className="main">
        <div className="section-block">
          <div className="section-title-row">
            <div className="section-secondary-title">資産情報の登録</div>
            {showLink && (
              <div
                className="link-primary align-right"
                onClick={() => {
                  handleClickOptionalAssetRenewal();
                }}>
                <div className="link-icon icon-switching icon-link-switching"></div>
                登録済み資産の更新
              </div>
            )}
          </div>
          <div className="banner-primary asset-input">
            <img
              src="/static/images/advisor/asset-input.svg"
              className="banner-primary-image"
              alt=""
            />
          </div>
          <div className="form-block">
            <div className="vertical-form-col">
              <div className="vertical-form-label">資産名・口座名</div>
              <div className="form-control-col">
                <input
                  type="text"
                  className="form-control form-fluid"
                  placeholder="○○銀行、タンス貯金、不動産など"
                  value={assetName}
                  onChange={event => setAssetName(event.currentTarget.value)}
                />
              </div>
            </div>
            <div className="vertical-form-col">
              <div className="vertical-form-label">
                金額（{formatDate(new Date(), 'yyyy年M月d日')}時点）
              </div>
              <div className="form-control-col">
                <InputNumber
                  className="form-control form-fluid form-number"
                  customValidate={value =>
                    customValidate(value.value, value.valueAsNumber)
                  }
                  defaultValue={null}
                  inputMode="numeric"
                  max={maxAmount}
                  min={minAmount}
                  onChange={value => setAssetAmount(value)}
                  placeholder="0"
                />
                <div className="unit">円</div>
              </div>
            </div>
            {!assetAmount.valid && (
              <div className="form-error-note" style={{ marginBottom: '10px' }}>
                <div className="icon-alert"></div>
                金額は{minAmount}円以上{formatNumber(maxAmount, {})}
                円以下の整数を入力してください
              </div>
            )}
            <div className="vertical-form-col">
              <div className="vertical-form-label">種別</div>
              <div className="form-select-control-col form-fluid">
                <select
                  className="appearance-none"
                  value={assetType}
                  onChange={value =>
                    setAssetType(
                      Number(value.currentTarget.value) as ManualAssetType
                    )
                  }>
                  {manualAssetTypes.map((x, i) => (
                    <option key={i} value={x.id}>
                      {x.title}
                    </option>
                  ))}
                </select>
                <div className="icon-chevron-down ml-auto text-lg text-bluegray-400"></div>
              </div>
            </div>
            <div className="section-third-note">
              ※口座連携していない資産を入力してください。重複登録にご注意ください。
            </div>
            {showLink && (
              <div className="link-row">
                <div
                  className="link-primary align-right cursor-pointer"
                  onClick={() => handleClickSecurityAccounts()}>
                  お持ちの口座を連携・確認する
                </div>
              </div>
            )}
          </div>
          <div className="btn-vertical-col">
            <Button
              disabled={
                disabled ||
                !assetName ||
                !assetAmount.valid ||
                assetAmount.value === 0
              }
              fluid
              onClick={() => handleRegister()}
              primary>
              登録
            </Button>
            <Button fluid onClick={() => cancel()} text>
              キャンセル
            </Button>
          </div>
        </div>
      </main>
    </>
  );
};

export default OptionalAssetRegistration;
